/**
 * Helpers for rendering and shipping the checklist PDF.
 *
 *   downloadChecklistPdf — generate the PDF in-browser and trigger a
 *                          standard download via <a download>.
 *   emailChecklistPdf    — generate the PDF, send it to the user's email
 *                          via the /api/checklist/email Worker route.
 *
 * Both helpers use the same render path (`renderChecklistPdfBlob`) so the
 * downloaded file and the emailed attachment are byte-identical. Network
 * layout differences would otherwise create silent divergence — that's a
 * support nightmare we want to avoid up front.
 */

import { pdf } from '@react-pdf/renderer';
import { ChecklistPdf, type ChecklistPdfProps } from './ChecklistPdf';

/** Render the ChecklistPdf component to a Blob. */
export async function renderChecklistPdfBlob(
  props: ChecklistPdfProps,
): Promise<Blob> {
  // pdf() is the imperative entry point: it accepts a React element,
  // mounts it inside react-pdf's renderer, and returns a streaming
  // instance with a `.toBlob()` that resolves once the layout is done.
  // JSX (not createElement) keeps the typed DocumentProps signature.
  return await pdf(<ChecklistPdf {...props} />).toBlob();
}

/** Save the rendered PDF to disk via a synthetic anchor click. */
export async function downloadChecklistPdf(
  props: ChecklistPdfProps,
  filename: string,
): Promise<void> {
  const blob = await renderChecklistPdfBlob(props);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    // Append-then-click is required in Firefox (the click is a no-op on
    // detached elements). Removed straight after to keep the DOM clean.
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revocation by a tick — Safari/iOS can race the click and
    // load the URL after the synchronous code finishes, which would
    // fail if the object URL is already gone.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Convert a Blob to a base64 string (without the data: prefix). */
async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  // Chunked conversion — String.fromCharCode(...array) overflows the call
  // stack at ~125k chars on V8. 32 KB chunks are well below that ceiling.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return typeof btoa === 'function' ? btoa(binary) : '';
}

export interface EmailChecklistArgs {
  email: string;
  countryCode: string;
  countryName: string;
  lang: 'en' | 'ru';
  filename?: string;
}

export interface EmailChecklistResult {
  ok: boolean;
  status: number;
  error?: string;
}

/** API base URL — same logic as `web/src/lib/auth-client.ts`. */
function apiBase(): string {
  return (
    (import.meta.env.VITE_API_URL as string | undefined) ||
    'http://localhost:8787'
  );
}

/**
 * Render the PDF in the browser, encode it to base64, then POST to the
 * Worker to be forwarded as an email attachment.
 */
export async function emailChecklistPdf(
  pdfProps: ChecklistPdfProps,
  args: EmailChecklistArgs,
): Promise<EmailChecklistResult> {
  const blob = await renderChecklistPdfBlob(pdfProps);
  const pdfBase64 = await blobToBase64(blob);

  const filename =
    args.filename ??
    `lodestar-${args.countryCode.toUpperCase()}-checklist.pdf`;

  let res: Response;
  try {
    res = await fetch(`${apiBase()}/api/checklist/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: args.email.trim().toLowerCase(),
        countryCode: args.countryCode.toUpperCase(),
        countryName: args.countryName,
        lang: args.lang,
        pdfBase64,
        filename,
      }),
    });
  } catch (err) {
    return { ok: false, status: 0, error: (err as Error).message || 'network_error' };
  }

  if (!res.ok) {
    let errorTag = `http_${res.status}`;
    try {
      const json = (await res.json()) as { error?: string };
      if (json?.error) errorTag = json.error;
    } catch {
      // Non-JSON error body — keep the http_NNN tag.
    }
    return { ok: false, status: res.status, error: errorTag };
  }

  return { ok: true, status: res.status };
}
